// Temporary Supabase Free plan limit. Raising this to 100 MB on Pro only
// requires changing this constant and the matching database/bucket limits.
export const PROJECT_FILE_LIMIT_MB = 50;
export const MAX_PROJECT_FILE_BYTES = PROJECT_FILE_LIMIT_MB * 1024 * 1024;

export const PROJECT_FILE_EXTENSIONS = [
  "pdf", "doc", "docx", "xls", "xlsx", "csv", "ppt", "pptx", "txt", "rtf",
  "jpg", "jpeg", "png", "webp", "gif",
  "mp3", "m4a", "wav", "aac", "ogg",
  "mp4", "mov", "webm", "m4v",
  "msg",
] as const;

const allowedExtensions = new Set<string>(PROJECT_FILE_EXTENSIONS);

export const PROJECT_FILE_MIME_TYPES: Record<string, readonly string[]> = {
  pdf: ["application/pdf"],
  doc: ["application/msword"],
  docx: ["application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
  xls: ["application/vnd.ms-excel", "application/msexcel"],
  xlsx: ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
  csv: ["text/csv", "application/vnd.ms-excel"],
  ppt: ["application/vnd.ms-powerpoint"],
  pptx: ["application/vnd.openxmlformats-officedocument.presentationml.presentation"],
  txt: ["text/plain"],
  rtf: ["application/rtf", "text/rtf"],
  jpg: ["image/jpeg"], jpeg: ["image/jpeg"], png: ["image/png"],
  webp: ["image/webp"], gif: ["image/gif"],
  mp3: ["audio/mpeg", "audio/mp3"],
  m4a: ["audio/mp4", "audio/x-m4a"],
  wav: ["audio/wav", "audio/x-wav"],
  aac: ["audio/aac"], ogg: ["audio/ogg"],
  mp4: ["video/mp4"], mov: ["video/quicktime"],
  webm: ["video/webm"], m4v: ["video/x-m4v", "video/mp4"],
  msg: ["application/vnd.ms-outlook", "application/octet-stream"],
};

export type ProjectFileValidation =
  | { ok: true; extension: string }
  | { ok: false; message: string };

export function projectFileExtension(name: string) {
  const dot = name.lastIndexOf(".");
  return dot > -1 ? name.slice(dot + 1).toLowerCase() : "";
}

export function validateProjectFile(
  file: Pick<File, "name" | "size"> & Partial<Pick<File, "type">>,
): ProjectFileValidation {
  if (file.size > MAX_PROJECT_FILE_BYTES)
    return { ok: false, message: "El archivo supera el límite máximo de 50 MB." };
  const extension = projectFileExtension(file.name);
  if (!allowedExtensions.has(extension))
    return {
      ok: false,
      message: "Este tipo de archivo no está permitido por seguridad.",
    };
  if (file.type && !PROJECT_FILE_MIME_TYPES[extension]?.includes(file.type.toLowerCase()))
    return { ok: false, message: "Este tipo de archivo no está permitido por seguridad." };
  return { ok: true, extension };
}

export function projectFileMimeType(file: Pick<File, "name" | "type">) {
  const extension = projectFileExtension(file.name);
  return file.type.toLowerCase() || PROJECT_FILE_MIME_TYPES[extension]?.[0] || "";
}

const OLE_COMPOUND_SIGNATURE = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];

export async function validateProjectFileContents(file: File): Promise<ProjectFileValidation> {
  const basic = validateProjectFile(file);
  if (!basic.ok || basic.extension !== "msg") return basic;
  const slice = file.slice(0, 8);
  const buffer = typeof slice.arrayBuffer === "function"
    ? await slice.arrayBuffer()
    : await new Promise<ArrayBuffer>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error ?? new Error("No se pudo leer el archivo."));
        reader.onload = () => resolve(reader.result as ArrayBuffer);
        reader.readAsArrayBuffer(slice);
      });
  const header = new Uint8Array(buffer);
  const isOutlookCompoundFile =
    header.length === OLE_COMPOUND_SIGNATURE.length &&
    OLE_COMPOUND_SIGNATURE.every((byte, index) => header[index] === byte);
  return isOutlookCompoundFile
    ? basic
    : { ok: false, message: "El archivo .msg no tiene una estructura Outlook válida." };
}
